import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { InsertWaterproofKitBody, UpdateWaterproofKitBody, WaterproofKit } from "./types/waterproofKits";

export const useWaterproofKitsApi = () => {
  const api = useApi();

  const getAdminWaterproofKit = useCallback(
    (waterproofKitId: string, signal: AbortSignal) => {
      const url = `/admin/settings/border/waterproof_kits/${waterproofKitId}`;
      return useSWRMutation<WaterproofKit, CustomError, string>(url, (url) => api.get<WaterproofKit>({ url, signal }));
    },
    [api],
  );

  const useAdminWaterproofKits = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/waterproof_kits`, query);
      return useSWRImmutable<Paginated<WaterproofKit>, CustomError, string>(url, (url) =>
        api.get<Paginated<WaterproofKit>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminWaterproofKits = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/waterproof_kits`, query);
      return api.get<Paginated<WaterproofKit>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminWaterproofKit = useCallback(
    async (body: InsertWaterproofKitBody): Promise<{ id: string }> => {
      const url = `/admin/settings/border/waterproof_kits`;
      const id = await api.post<{ id: string }, InsertWaterproofKitBody>(url, body);
      mutateCache([url, `/admin/settings/border/bases`]);
      return id;
    },
    [api],
  );

  const updateAdminWaterproofKit = useCallback(
    async (waterproofKitId: string, body: UpdateWaterproofKitBody): Promise<void> => {
      const url = `/admin/settings/border/waterproof_kits/${waterproofKitId}`;
      await api.put<UpdateWaterproofKitBody>(url, body);
      mutateCache([`/admin/settings/border/waterproof_kits`, `/admin/settings/border/bases`]);
    },
    [api],
  );

  const updateAdminWaterproofKitVisibility = useCallback(
    async (waterproofKitId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/border/waterproof_kits/${waterproofKitId}/visibility`;
      (await api.put<UpdateVisibilityRequestBody>(url, visibility),
        mutateCache([`/admin/settings/border/waterproof_kits`, `/admin/settings/border/bases`]));
    },
    [api],
  );

  const updateAdminWaterproofKitOrder = useCallback(
    async (waterproofKitId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/border/waterproof_kits/${waterproofKitId}/order`;
      (await api.put<UpdateOrderRequestBody>(url, order),
        mutateCache([`/admin/settings/border/waterproof_kits`, `/admin/settings/border/bases`]));
    },
    [api],
  );

  const deleteAdminWaterproofKit = useCallback(
    async (waterproofKit: WaterproofKit): Promise<void> => {
      const url = `/admin/settings/border/waterproof_kits/${waterproofKit.item.id}`;
      await api.del(url);
      mutateCache([`/admin/settings/border/waterproof_kits`, `/admin/settings/border/bases`]);
    },
    [api],
  );

  return {
    getAdminWaterproofKit,
    insertAdminWaterproofKit,
    updateAdminWaterproofKit,
    updateAdminWaterproofKitVisibility,
    updateAdminWaterproofKitOrder,
    deleteAdminWaterproofKit,
    useAdminWaterproofKits,
    getAdminWaterproofKits,
  };
};
